import {
  Calendar,
  Check,
  Copy,
  Edit3,
  Eye,
  FileText,
  Globe,
  MoreVertical,
  Sparkles,
  Trash2,
  Upload,
} from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "../../contexts/LanguageContext";
import { showToastSuccess } from "../../shared/alertService";
import { cn } from "../../utils/cn";
import { getPlatformConfig } from "../../utils/platformIcons";
import Badge from "../ui/Badge";
import Card from "../ui/Card";
import Tooltip from "../ui/Tooltip";

export interface ContentEntry {
  id: number;
  name: string;
  type: "url" | "text" | "prompt" | "file";
  original_data: string;
  processed_content: any;
  platforms: string[];
  business_type: string;
  created_at: string;
  status: string;
}

interface ContentCardProps {
  content: ContentEntry;
  onPreview?: (content: ContentEntry) => void;
  onEdit?: (content: ContentEntry) => void;
  onDelete?: (content: ContentEntry) => void;
}

const getTypeIcon = (type: string) => {
  switch (type?.toLowerCase()) {
    case "url":
      return <Globe className="h-4 w-4" />;
    case "text":
      return <FileText className="h-4 w-4" />;
    case "prompt":
      return <Sparkles className="h-4 w-4" />;
    case "file":
      return <Upload className="h-4 w-4" />;
    default:
      return <FileText className="h-4 w-4" />;
  }
};

const getTypeLabel = (type: string, t: (key: string) => string) => {
  switch (type?.toLowerCase()) {
    case "url":
      return t("URL/Site");
    case "text":
      return t("Texto");
    case "prompt":
      return t("Prompt IA");
    case "file":
      return t("Arquivo");
    default:
      return type || t("Conteúdo");
  }
};

const getTypeColors = (type: string) => {
  switch (type) {
    case "url":
      return {
        border: "border-blue-200",
        hoverBorder: "group-hover:border-blue-400",
        accent: "bg-blue-600",
        text: "text-blue-600",
        light: "bg-blue-50",
        glow: "group-hover:shadow-[0_15px_40px_rgba(59,130,246,0.12)]",
      };
    case "text":
      return {
        border: "border-emerald-200",
        hoverBorder: "group-hover:border-emerald-400",
        accent: "bg-emerald-600",
        text: "text-emerald-600",
        light: "bg-emerald-50",
        glow: "group-hover:shadow-[0_15px_40px_rgba(16,185,129,0.12)]",
      };
    case "prompt":
      return {
        border: "border-purple-200",
        hoverBorder: "group-hover:border-purple-400",
        accent: "bg-purple-600",
        text: "text-purple-600",
        light: "bg-purple-50",
        glow: "group-hover:shadow-[0_15px_40px_rgba(139,92,246,0.12)]",
      };
    case "file":
      return {
        border: "border-orange-200",
        hoverBorder: "group-hover:border-orange-400",
        accent: "bg-orange-600",
        text: "text-orange-600",
        light: "bg-orange-50",
        glow: "group-hover:shadow-[0_15px_40px_rgba(249,115,22,0.12)]",
      };
    default:
      return {
        border: "border-purple-200",
        hoverBorder: "group-hover:border-purple-400",
        accent: "bg-gray-600",
        text: "text-gray-600",
        light: "bg-gray-50",
        glow: "group-hover:shadow-[0_15px_40px_rgba(107,114,128,0.12)]",
      };
  }
};

const ContentCard: React.FC<ContentCardProps> = ({
  content,
  onPreview,
  onEdit,
  onDelete,
}) => {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const colors = getTypeColors(content.type);

  // Fechar menu ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        buttonRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === "Escape") setIsMenuOpen(false);
      };
      document.addEventListener("keydown", handleEscape);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
        document.removeEventListener("keydown", handleEscape);
      };
    }
  }, [isMenuOpen]);

  const handleCardClick = (e: React.MouseEvent) => {
    if (
      buttonRef.current?.contains(e.target as Node) ||
      menuRef.current?.contains(e.target as Node) ||
      (e.target as HTMLElement).closest("button:not(.preview-trigger)")
    ) {
      return;
    }
    if (onPreview) {
      onPreview(content);
    }
  };

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!content.original_data) return;

    navigator.clipboard.writeText(content.original_data);
    setIsCopied(true);
    showToastSuccess(t("Conteúdo copiado!"));

    setTimeout(() => setIsCopied(false), 2000);
  };

  return (
    <Card
      className={cn(
        "group relative flex flex-col h-full bg-white transition-all duration-300",
        "border border-purple-200 hover:border-purple-300",
        colors.glow,
        "hover:scale-[1.02]",
        "cursor-pointer overflow-hidden p-0 rounded-2xl",
        isMenuOpen && "z-40",
      )}
      onClick={handleCardClick}
    >
      {/* Indicador Lateral */}
      <div
        className={cn(
          "absolute left-0 top-0 bottom-0 w-1 z-20 transition-all duration-300",
          colors.accent,
          "group-hover:w-1.5",
        )}
      />

      {/* Dropdown Menu Toggle */}
      <div className="absolute top-1 right-1 z-30">
        <Tooltip content={t("Mais opções")} position="left">
          <button
            ref={buttonRef}
            onClick={(e) => {
              e.stopPropagation();
              setIsMenuOpen(!isMenuOpen);
            }}
            className={cn(
              "p-1.5 rounded-xl transition-all duration-200",
              "hover:bg-gray-100 active:scale-95",
              "text-gray-400 hover:text-gray-900 bg-white/90 shadow-xs border border-purple-200",
              isMenuOpen &&
                "bg-purple-600 text-white border-purple-600 shadow-md",
            )}
          >
            <MoreVertical className="h-4 w-4" />
          </button>
        </Tooltip>

        {/* Dropdown de OpÃ§Ãµes */}
        {isMenuOpen && (
          <div
            ref={menuRef}
            className="absolute right-0 mt-2 w-48 bg-white/95 backdrop-blur-xl rounded-xl shadow-xl border border-purple-200 py-1.5 z-60 animate-modal-scale-in"
            role="menu"
            onClick={(e) => e.stopPropagation()}
          >
            {onEdit && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsMenuOpen(false);
                  onEdit(content);
                }}
                className="w-full px-3 py-2 text-left text-xs font-bold text-gray-700 hover:bg-purple-600 hover:text-white transition-all flex items-center gap-2.5 group/item"
                role="menuitem"
              >
                <div className="p-1.5 rounded-lg bg-purple-50 group-hover/item:bg-white/20 transition-colors">
                  <Edit3 className="h-3.5 w-3.5 text-purple-600 group-hover/item:text-white" />
                </div>
                {t("Editar")}
              </button>
            )}

            {onDelete && (
              <>
                <div className="h-px bg-gray-100 my-1 mx-2" />
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsMenuOpen(false);
                    onDelete(content);
                  }}
                  className="w-full px-3 py-2 text-left text-xs font-bold text-red-600 hover:bg-red-600 hover:text-white transition-all flex items-center gap-2.5 group/del"
                  role="menuitem"
                >
                  <div className="p-1.5 rounded-lg bg-red-50 group-hover/del:bg-white/20 transition-colors">
                    <Trash2 className="h-3.5 w-3.5 text-red-600 group-hover/del:text-white" />
                  </div>
                  {t("Excluir")}
                </button>
              </>
            )}
          </div>
        )}
      </div>

      <div className="p-3.5 flex flex-col h-full gap-3 relative">
        {/* CabeÃ§alho do Card */}
        <div className="flex items-start gap-2.5">
          {/* Ãcone */}
          <div
            className={cn(
              "shrink-0 w-11 h-11 flex items-center justify-center rounded-xl shadow-md ring-2 ring-white transition-all duration-300 group-hover:scale-105",
              colors.accent,
              "text-white",
            )}
          >
            {getTypeIcon(content.type)}
          </div>

          <div className="flex-1 min-w-0 pt-0.5">
            <div className="flex flex-wrap gap-1.5 mb-1.5">
              <Badge
                variant="outline"
                size="sm"
                className={cn(
                  "font-bold uppercase tracking-wider text-[9px] px-2 py-0.5",
                  colors.text,
                  colors.light,
                  "border-transparent",
                )}
              >
                {getTypeLabel(content.type, t)}
              </Badge>
              {content.business_type && (
                <Badge
                  variant="secondary"
                  size="sm"
                  className="font-bold text-[9px] bg-gray-100 text-gray-500 border-transparent uppercase px-2 py-0.5"
                >
                  {content.business_type}
                </Badge>
              )}
            </div>
            <h3 className="text-gray-900 font-bold text-sm sm:text-base leading-tight line-clamp-2 group-hover:text-purple-700 transition-colors duration-200">
              {content.name}
            </h3>
          </div>
        </div>

        {/* Snippet de Conteúdo */}
        <div className="flex-1 relative group/snippet">
          <p className="text-[11px] sm:text-xs text-gray-500 line-clamp-3 leading-relaxed font-medium bg-gray-50/50 p-3 rounded-xl border border-purple-200 group-hover:border-purple-200 transition-all relative overflow-hidden">
            {content.original_data || t("Sem prévia disponível")}
            <span className="absolute bottom-0 left-0 right-0 h-4 bg-linear-to-t from-gray-50/50 to-transparent group-hover:from-white/0" />
          </p>

          {/* Copiar RÃ¡pido */}
          <div className="absolute top-1.5 right-1.5 opacity-0 group-hover/snippet:opacity-100 transition-opacity">
            <Tooltip content={isCopied ? t("Pronto!") : t("Copiar")}>
              <button
                onClick={handleCopy}
                className={cn(
                  "p-1.5 rounded-lg shadow-xs border transition-all active:scale-90",
                  isCopied
                    ? "bg-emerald-500 text-white border-emerald-500"
                    : "bg-white text-gray-400 border-purple-200 hover:text-purple-600",
                )}
              >
                {isCopied ? (
                  <Check className="h-3 w-3" />
                ) : (
                  <Copy className="h-3 w-3" />
                )}
              </button>
            </Tooltip>
          </div>
        </div>

        {/* Footer do Card */}
        <div className="flex flex-col gap-3 pt-3.5 border-t border-purple-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5 text-[10px] text-gray-400 font-bold bg-gray-50 px-2 py-1 rounded-lg border border-purple-200">
              <Calendar className="h-3 w-3" />
              {new Date(content.created_at).toLocaleDateString("pt-BR", {
                day: "2-digit",
                month: "short",
              })}
            </div>

            {content.platforms && content.platforms.length > 0 && (
              <div className="flex -space-x-1.5">
                {content.platforms.slice(0, 3).map((platform, i) => {
                  const config = getPlatformConfig(platform);
                  const Icon = config.icon;
                  return (
                    <Tooltip key={i} content={config.name}>
                      <div className="w-6 h-6 rounded-full bg-white border border-purple-200 flex items-center justify-center p-1.5 shadow-xs relative z-1 transition-transform hover:scale-[1.02]">
                        <Icon className="h-full w-full opacity-70" />
                      </div>
                    </Tooltip>
                  );
                })}
                {content.platforms.length > 3 && (
                  <div className="w-6 h-6 rounded-full bg-purple-600 border border-white flex items-center justify-center text-[9px] font-bold text-white relative z-0">
                    +{content.platforms.length - 3}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* BotÃ£o Visual */}
          <div className="h-8 overflow-hidden relative rounded-lg bg-gray-50/50 group-hover:bg-purple-50/50 transition-colors">
            <div className="absolute inset-0 z-10 flex items-center justify-center gap-2 bg-purple-50/50 text-purple-600 font-bold text-[10px] transition-all duration-300 translate-y-8 group-hover:translate-y-0 uppercase tracking-wider">
              <Eye className="h-3 w-3" />
              {t("Ver Detalhes")}
            </div>
            <div className="absolute inset-0 flex items-center justify-center text-[9px] font-bold text-gray-300 tracking-widest transition-all duration-300 group-hover:opacity-0 uppercase">
              {t("Conteúdo Salvo")}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default ContentCard;
